import React, {useEffect, useState} from 'react';
import BarChart from './BarChart';
import HostingCustodiansTable from './HostingCustodiansTable';
import {CustomAurDataGridColDef} from './HostingVolumeModel';
import {t} from 'i18next';
import {fetchHostingVolumeDatasetSummary} from '../../../api/ReportingManagement';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {failedToLoadStatusCodes} from '../../../constants/constants';
import {convertToDecimalPlace} from '../../../utils/inTakeAuditReportUtils';

interface DatasetItem {
  dataset: string;
  total: number;
}
interface HostingVolumeReportProps {
  setSelectedRowData: any;
  apiData: any;
}

const HostingVolumeReport: React.FC<HostingVolumeReportProps> = ({
  setSelectedRowData,
  apiData,
}) => {
  const [values, setValues] = useState<DatasetItem[]>([]);
  const [isFailedToLoadContent, setIsFailedToLoadContent] =
    useState<boolean>(false);
  const {clientId, projectId, workspaceId} = useSelector(
    (state: RootState) => state.reports
  );
  const columns: CustomAurDataGridColDef[] = [
    {
      field: 'custodianName',
      headerName: t('column.name'),
      headerClassName: 'table-header-cell-hosting',
      width: 220,
      sortable: true,
      isChart: false,
    },
    {
      field: 'total',
      headerName: t('column.totalDocs'),
      flex: 0.26,
      cellClassName: 'custodian-name-cell',
      headerClassName: 'table-header-cell-hosting',
      width: 128,
      sortable: true,
      isChart: false,
      valueGetter: (value) => convertToDecimalPlace(value),
    },
    {
      field: 'loaded',
      headerName: t('reports.hostingVolume.cumulativeReport.email'),
      flex: 0.26,
      cellClassName: 'custodian-name-cell',
      headerClassName: 'table-header-cell-hosting',
      width: 140,
      sortable: true,
      isChart: true,
      max: 130,
      color: '#7cb5ec',
    },
    {
      field: 'released',
      headerName: t(
        'reports.hostingVolume.cumulativeReport.externallyProcessed'
      ),
      flex: 0.26,
      cellClassName: 'custodian-name-cell',
      headerClassName: 'table-header-cell-hosting',
      width: 140,
      sortable: true,
      isChart: true,
      max: 130,
      color: '#7cb5ec',
    },
  ];
  const fetchDatasetSummaryDetails = async () => {
    try {
      setIsFailedToLoadContent(false);
      const result = await fetchHostingVolumeDatasetSummary(
        clientId,
        projectId,
        workspaceId
      );
      setValues(result.data.data.datasetSummary);
    } catch (e: any) {
      if (failedToLoadStatusCodes.includes(e.status))
        setIsFailedToLoadContent(true);
    }
  };
  useEffect(() => {
    if (clientId && projectId && workspaceId) {
      fetchDatasetSummaryDetails();
    }
  }, [clientId, projectId, workspaceId]);

  const categories = values.map((item) => item.dataset);
  const totalGigaByte = values.map((item) => item.total);

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gap: '20px',
        }}
      >
        <div style={{gridColumn: 'span 8'}}>
          <HostingCustodiansTable
            title={t('reports.hostingVolume.custodiansTitle')}
            columns={columns}
            setSelectedRowData={setSelectedRowData}
            apiData={apiData}
          />
        </div>
        <div style={{gridColumn: 'span 4'}}>
          {isFailedToLoadContent ? (
            <AurDataGridErrorOverlay onRefresh={fetchDatasetSummaryDetails} />
          ) : (
            <BarChart
              title={t('reports.hostingVolume.barchartTitle')}
              categories={categories}
              data={totalGigaByte}
              yAxisLabel={t('reports.hostingVolume.barchartY')}
              xAxisLabel=""
              dataTestId="bar-chart"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default HostingVolumeReport;
